import $ from "jquery";
import moment from "moment";
import validator from "validator";

("use strict");

class FormValidator {
	constructor(formSelector, options = {}) {
		this.form = $(formSelector);
		this.options = {
			errorClass: "is-invalid",
			successClass: "is-valid",
			errorWrapper: "div",
			errorWrapperClass: "invalid-feedback",
			errorTemplate: "span",
			...options,
		};
		this.validators = new Map();
		this.errors = new Map();

		this.initializeFields();
		this.bindEvents();
	}

	initializeFields() {
		const self = this;

		this.form.find("input, textarea, select").each(function () {
			const $field = $(this);
			const fieldValidators = self.parseValidationAttributes($field);

			if (fieldValidators.length > 0) {
				self.validators.set($field[0], fieldValidators);
			}
		});
	}

	parseValidationAttributes($field) {
		const validators = [];
		const data = $field.data();

		// Required validation
		if ($field.attr("required") || data.validatorRequired !== undefined) {
			validators.push({
				type: "required",
				message: data.validatorRequiredMessage || "This field is required",
			});
		}

		// Email validation
		if (data.validatorEmail !== undefined || $field.attr("type") === "email") {
			validators.push({
				type: "email",
				message:
					data.validatorEmailMessage || "Please enter a valid email address",
			});
		}

		// Number/Integer validation
		if (data.validatorType === "number" || $field.attr("type") === "number") {
			validators.push({
				type: "number",
				message: data.validatorTypeMessage || "Please enter a valid number",
			});
		}

		// Range validation
		if (data.validatorRange) {
			const range = this.parseRange(data.validatorRange);
			validators.push({
				type: "range",
				min: range.min,
				max: range.max,
				message:
					data.validatorRangeMessage ||
					`Value must be between ${range.min} and ${range.max}`,
			});
		}

		// Min validation
		if (data.validatorMin !== undefined) {
			validators.push({
				type: "min",
				value: parseFloat(data.validatorMin),
				message:
					data.validatorMinMessage ||
					`Value must be at least ${data.validatorMin}`,
			});
		}

		// Minlength validation
		if (data.validatorMinlength !== undefined) {
			validators.push({
				type: "minlength",
				value: parseInt(data.validatorMinlength),
				message:
					data.validatorMinlengthMessage ||
					`Must be at least ${data.validatorMinlength} characters`,
			});
		}

		// Equal to validation (password confirmation)
		if (data.validatorEqualto) {
			validators.push({
				type: "equalto",
				target: data.validatorEqualto,
				message: data.validatorEqualtoMessage || "Values do not match",
			});
		}

		// Custom date comparison
		if (data.validatorDateIsSameOrAfter) {
			validators.push({
				type: "dateIsSameOrAfter",
				target: data.validatorDateIsSameOrAfter,
				message:
					data.validatorDateIsSameOrAfterMessage ||
					"Date must be same or after the reference date",
			});
		}

		// Custom zero or in range
		if (data.validatorZeroOrInRange) {
			const range = this.parseRange(data.validatorZeroOrInRange);
			validators.push({
				type: "zeroOrInRange",
				min: range.min,
				max: range.max,
				message:
					data.validatorZeroOrInRangeMessage ||
					`Value must be 0 or between ${range.min} and ${range.max}`,
			});
		}

		return validators;
	}

	parseRange(rangeStr) {
		// Handle both "[min, max]" and simple "min,max" formats
		const cleaned = rangeStr.toString().replace(/[[\]]/g, "");
		const parts = cleaned.split(",").map((s) => parseFloat(s.trim()));
		return { min: parts[0], max: parts[1] };
	}

	validateField($field) {
		const fieldValidators = this.validators.get($field[0]);
		if (!fieldValidators) return true;

		// Skip disabled fields (matches HTML5 validation behavior)
		if ($field.prop("disabled")) return true;

		const value = $field.val();
		const errors = [];

		for (const validatorConfig of fieldValidators) {
			const isValid = this.runValidator($field, value, validatorConfig);
			if (!isValid) {
				errors.push(validatorConfig.message);
			}
		}

		this.displayFieldErrors($field, errors);

		if (errors.length > 0) {
			this.errors.set($field[0], errors);
			return false;
		} else {
			this.errors.delete($field[0]);
			return true;
		}
	}

	runValidator($field, value, config) {
		switch (config.type) {
			case "required":
				return (
					value !== undefined &&
					value !== null &&
					value.toString().trim() !== ""
				);

			case "email":
				return !value || validator.isEmail(value);

			case "number":
				return !value || validator.isNumeric(value.toString());

			case "range": {
				if (!value) return true;
				const num = parseFloat(value);
				return !isNaN(num) && num >= config.min && num <= config.max;
			}

			case "min": {
				if (!value) return true;
				const minNum = parseFloat(value);
				return !isNaN(minNum) && minNum >= config.value;
			}

			case "minlength":
				return !value || value.toString().length >= config.value;

			case "equalto": {
				const targetValue = $(config.target).val();
				return value === targetValue;
			}

			case "dateIsSameOrAfter": {
				if (!value) return true;
				const targetDate = $(config.target).val();
				if (!targetDate) return true;
				return moment(value).isSameOrAfter(moment(targetDate));
			}

			case "zeroOrInRange": {
				if (!value) return true;
				const zeroRangeNum = parseFloat(value);
				if (isNaN(zeroRangeNum)) return false;
				return (
					zeroRangeNum === 0 ||
					(zeroRangeNum >= config.min && zeroRangeNum <= config.max)
				);
			}

			default:
				return true;
		}
	}

	displayFieldErrors($field, errors) {
		// Remove existing validation classes and error messages
		$field.removeClass(
			`${this.options.errorClass} ${this.options.successClass}`,
		);

		// Find or create error container
		const $errorContainer = this.getErrorContainer($field);
		$errorContainer.empty();

		if (errors.length > 0) {
			// Add error class
			$field.addClass(this.options.errorClass);

			// Display error messages
			errors.forEach((error) => {
				const $errorElement = $(
					`<${this.options.errorTemplate}>${error}</${this.options.errorTemplate}>`,
				);
				$errorContainer.append($errorElement);
			});
		} else {
			// Add success class
			$field.addClass(this.options.successClass);
		}
	}

	getErrorContainer($field) {
		const data = $field.data();

		// Check for custom error container
		if (data.validatorErrorsContainer) {
			return $(data.validatorErrorsContainer);
		}

		// Look for existing error container
		let $container = $field.siblings(`.${this.options.errorWrapperClass}`);

		if ($container.length === 0) {
			// Create new error container
			$container = $(
				`<${this.options.errorWrapper} class="${this.options.errorWrapperClass}"></${this.options.errorWrapper}>`,
			);
			$field.after($container);
		}

		return $container;
	}

	bindEvents() {
		const self = this;

		// Validate on blur
		this.form.on("blur", "input, textarea, select", function () {
			self.validateField($(this));
		});

		// Clear errors on focus/input
		this.form.on("focus input", "input, textarea, select", function () {
			const $field = $(this);
			if (self.errors.has($field[0])) {
				// Remove error state but don't validate yet
				$field.removeClass(self.options.errorClass);
				self.getErrorContainer($field).empty();
			}
		});
	}

	isValid() {
		let isFormValid = true;

		// Validate all fields
		this.form.find("input, textarea, select").each((index, field) => {
			const $field = $(field);
			if (this.validators.has(field)) {
				const fieldValid = this.validateField($field);
				if (!fieldValid) {
					isFormValid = false;
				}
			}
		});

		return isFormValid;
	}

	validate() {
		return this.isValid();
	}

	reset() {
		this.errors.clear();
		this.form
			.find("input, textarea, select")
			.removeClass(`${this.options.errorClass} ${this.options.successClass}`);
		this.form.find(`.${this.options.errorWrapperClass}`).empty();
	}

	destroy() {
		this.form.off("blur focus input", "input, textarea, select");
		this.reset();
	}
}

// Factory function to maintain compatibility with existing FormValidator API
const createFormValidator = (selector, options = {}) =>
	new FormValidator(selector, options);

export default createFormValidator;
export { FormValidator };
